import { supabase, isConfigured } from './supabase';
import type { HostingPlan, UserProfile, ToursSubscriptionTier } from '../types';
import { TOUR_SUBSCRIPTION_TIERS } from '../types';

export const HOSTING_CREDITS: Record<HostingPlan, number> = {
  '7d': 2,
  '30d': 3,
  '6mo': 6,
  '12mo': 10,
  'slot': 0,
};

export interface HostingResult {
  success: boolean;
  expires_at?: string;
  credits_remaining?: number;
  plan?: HostingPlan;
  error?: string;
}

export const activatePlanosHosting = async (
  projectId: string,
  userId: string,
  plan: HostingPlan
): Promise<HostingResult> => {
  if (!isConfigured) return { success: false, error: 'Supabase not configured' };
  const credits = HOSTING_CREDITS[plan];
  if (!credits) return { success: false, error: 'Invalid hosting plan' };

  try {
    const { data, error } = await supabase.rpc('activate_planos_hosting', {
      p_project_id: projectId,
      p_user_id: userId,
      p_plan: plan,
      p_credits: credits,
    });

    if (error) {
      console.error('[Planos] Hosting activation error:', error);
      return { success: false, error: error.message };
    }

    const result = data as any;
    if (!result.success) {
      return { success: false, error: result.error };
    }

    return {
      success: true,
      expires_at: result.expires_at,
      credits_remaining: result.credits_remaining,
      plan: result.plan,
    };
  } catch (err: any) {
    console.error('[Planos] Hosting activation failed:', err);
    return { success: false, error: err.message };
  }
};

export const getDaysRemaining = (expiresAt: string | null, permanent?: boolean): number | null => {
  if (permanent) return Infinity;
  if (!expiresAt) return null;
  const now = new Date();
  const expiry = new Date(expiresAt);
  const diff = expiry.getTime() - now.getTime();
  if (diff <= 0) return 0;
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

export const formatDaysRemaining = (days: number | null): string => {
  if (days === null) return 'No hosting';
  if (days === Infinity) return 'Permanent';
  if (days === 0) return 'Expired';
  if (days === 1) return '1 day left';
  if (days <= 7) return `${days} days left`;
  if (days <= 30) return `${Math.ceil(days / 7)} weeks left`;
  return `${Math.round(days / 30)} months left`;
};

export const getHostingStatusColor = (
  status: string,
  daysRemaining: number | null
): { bg: string; text: string; dot: string } => {
  if (status === 'active' && daysRemaining !== null && daysRemaining <= 7) {
    return { bg: 'bg-amber-900/30', text: 'text-amber-400', dot: 'bg-amber-400' };
  }
  if (status === 'active') {
    return { bg: 'bg-green-900/30', text: 'text-green-400', dot: 'bg-green-400' };
  }
  if (status === 'expired') {
    return { bg: 'bg-red-900/30', text: 'text-red-400', dot: 'bg-red-400' };
  }
  return { bg: 'bg-slate-700', text: 'text-slate-300', dot: 'bg-slate-400' };
};

/* ─── Shared active slots (360 Tours + Planos) ───────────────────────────── */

export interface ActiveSlotCount {
  total: number;
  tours_count: number;
  planos_count: number;
  imagemotion_count: number;
  videomotion_count: number;
}

export const getActiveSlotCount = async (userId: string): Promise<ActiveSlotCount> => {
  if (!isConfigured) return { total: 0, tours_count: 0, planos_count: 0, imagemotion_count: 0, videomotion_count: 0 };
  try {
    const { data, error } = await supabase.rpc('get_unified_active_slot_count', { p_user_id: userId });
    if (error) return { total: 0, tours_count: 0, planos_count: 0, imagemotion_count: 0, videomotion_count: 0 };
    const r = data as { total?: number; tours_count?: number; planos_count?: number; imagemotion_count?: number; videomotion_count?: number };
    return {
      total: r?.total ?? 0,
      tours_count: r?.tours_count ?? 0,
      planos_count: r?.planos_count ?? 0,
      imagemotion_count: r?.imagemotion_count ?? 0,
      videomotion_count: r?.videomotion_count ?? 0,
    };
  } catch {
    return { total: 0, tours_count: 0, planos_count: 0, imagemotion_count: 0, videomotion_count: 0 };
  }
};

export interface CanUserPublishResult {
  allowed: boolean;
  activeCount: number;
  limit: number;
  tier: ToursSubscriptionTier;
}

export const canUserPublish = async (
  userId: string,
  profile?: Pick<UserProfile, 'tours_subscription_tier'> | null
): Promise<CanUserPublishResult> => {
  const tier: ToursSubscriptionTier = profile?.tours_subscription_tier ?? 'free';
  const tierConfig = TOUR_SUBSCRIPTION_TIERS.find((t) => t.tier === tier);
  const limit = tierConfig?.activeSlotLimit ?? 0;
  const { total: activeCount } = await getActiveSlotCount(userId);
  const allowed = limit === 0 || activeCount < limit;
  return { allowed, activeCount, limit, tier };
};

export const useActiveSlotForPlanos = async (
  projectId: string,
  userId: string
): Promise<{ success: boolean; error?: string; expires_at?: string }> => {
  if (!isConfigured) return { success: false, error: 'Not configured' };
  try {
    const { data, error } = await supabase.rpc('use_active_slot_for_planos', {
      p_project_id: projectId,
      p_user_id: userId,
    });
    if (error) return { success: false, error: error.message };
    const r = data as { success?: boolean; error?: string; expires_at?: string };
    if (!r?.success) return { success: false, error: r?.error ?? 'Failed' };
    return { success: true, expires_at: r.expires_at };
  } catch (err: unknown) {
    return { success: false, error: (err as Error)?.message ?? 'Failed' };
  }
};
